import type { FC } from 'react';
import { useContext, useCallback, memo, useRef } from 'react';
import { x2c } from '@gridsheet/web';
import { getLabel } from '@gridsheet/web';
import { between, zoneToArea } from '@gridsheet/web';
import { Context } from '../store';
import {
  choose,
  drag,
  select,
  selectCols,
  setAutofillDraggingTo,
  setColumnMenu,
  setContextMenuPosition,
  setDragging,
  setEditingAddress,
  setResizingPositionX,
  submitAutofill,
  write,
} from '../store/actions';
import { DEFAULT_WIDTH } from '@gridsheet/web';
import { operations as prevention } from '@gridsheet/web';
import { insertRef } from '@gridsheet/web';
import { focus } from '@gridsheet/web';
import { isXSheetFocused } from '../store/helpers';
import { ScrollHandle } from './ScrollHandle';
import { isTouching, safePreventDefault } from '../lib/events';
import { useDebounceCallback } from '../lib/hooks';

type Props = {
  x: number;
};

export const HeaderCellTop: FC<Props> = memo(({ x }) => {
  const colId = x2c(x);
  const { store, dispatch } = useContext(Context);

  const {
    sheetReactive: sheetRef,
    editingAddress,
    choosing,
    selectingZone,
    topHeaderSelecting,
    editorRef,
    autofillDraggingTo,
    dragging,
    contextMenu,
    columnMenuState,
  } = store;
  const sheet = sheetRef.current;

  const col = sheet?.getCell({ y: 0, x }, { resolution: 'SYSTEM' });
  const width = col?.width || DEFAULT_WIDTH;
  const hasFilter = !!(col?.filter && col.filter.conditions.length > 0);

  const xSheetFocused = isXSheetFocused(store);
  const lastFocused = sheet?.registry.lastFocused;

  const editingAnywhere = !!(sheet?.registry.editingAddress || editingAddress);

  const writeCell = useCallback(
    (value: string) => {
      dispatch(write({ value, point: choosing }));
    },
    [choosing],
  );

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    dispatch(setResizingPositionX([x, e.clientX, e.clientX]));
    e.stopPropagation();
    safePreventDefault(e);
  }, []);

  const handleDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.stopPropagation();
      safePreventDefault(e);

      if (!isTouching(e) || !sheet) {
        return false;
      }

      if (dragging) {
        return false;
      }

      // Single column selection only for touch events
      if (e.type.startsWith('touch')) {
        // Blur the input field to commit current value when selecting via touch
        if (editingAnywhere && editorRef.current) {
          editorRef.current.blur();
        }
        dispatch(choose({ y: 1, x }));
        dispatch(select({ startY: 1, startX: x, endY: sheet.numRows, endX: x }));
        return true;
      }

      dispatch(select({ startY: 1, startX: x, endY: -1, endX: x }));
      const fullAddress = `${sheet.sheetPrefix(!xSheetFocused)}${colId}:${colId}`;
      if (editingAnywhere) {
        const inserted = insertRef({ input: lastFocused || null, ref: fullAddress });
        if (inserted) {
          dispatch(select({ startY: sheet.numRows, startX: x, endY: 0, endX: x }));
          return false;
        }
      }

      let startX = e.shiftKey ? selectingZone.startX : x;
      if (startX === -1) {
        startX = choosing.x;
      }

      dispatch(
        selectCols({
          range: { start: startX, end: x },
          numRows: sheet.numRows,
        }),
      );

      if (editingAnywhere) {
        writeCell(lastFocused?.value ?? '');
      }
      dispatch(setEditingAddress(''));
      dispatch(setDragging(true));
      focus(editorRef.current);

      if (autofillDraggingTo) {
        return false;
      }
      return true;
    },
    [
      dragging,
      editingAnywhere,
      xSheetFocused,
      colId,
      lastFocused,
      selectingZone,
      choosing,
      autofillDraggingTo,
      editorRef,
    ],
  );

  const handleDragEnd = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.stopPropagation();
      if (e.type.startsWith('touch')) {
        return;
      }

      safePreventDefault(e);
      dispatch(setDragging(false));
      if (autofillDraggingTo) {
        focus(editorRef.current);
        return false;
      }
    },
    [autofillDraggingTo],
  );

  const handleDragging = useDebounceCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isTouching(e) || !sheet) {
      return false;
    }

    if (e.type.startsWith('touch')) {
      return false;
    }

    safePreventDefault(e);
    e.stopPropagation();

    if (autofillDraggingTo) {
      dispatch(setAutofillDraggingTo({ y: 1, x }));
      return false;
    }

    if (editingAnywhere) {
      const newArea = zoneToArea({ ...selectingZone, endY: 1, endX: x });
      const [left, right] = [x2c(newArea.left), x2c(newArea.right)];
      const fullRange = `${sheet.sheetPrefix(!xSheetFocused)}${left}:${right}`;
      insertRef({ input: lastFocused || null, ref: fullRange });
    }

    if (autofillDraggingTo == null) {
      const { startY } = selectingZone;
      if (startY === 1) {
        dispatch(drag({ y: sheet.numRows, x }));
      } else {
        dispatch(drag({ y: 1, x }));
      }
    }
    return false;
  }, 100);

  if (!sheet) {
    return (
      <th data-x={x} className="gs-th gs-th-top gs-hidden">
        <div className="gs-th-inner-wrap">
          <div className="gs-th-inner">
            <ScrollHandle style={{ position: 'absolute' }} vertical={-1} />
            <div className="gs-resizer"></div>
          </div>
        </div>
      </th>
    );
  }

  return (
    <th
      data-x={x}
      className={`gs-th gs-th-top ${choosing.x === x ? 'gs-choosing' : ''} ${
        between({ start: selectingZone.startX, end: selectingZone.endX }, x)
          ? topHeaderSelecting
            ? 'gs-th-selecting'
            : 'gs-selecting'
          : ''
      }`}
      style={{ ...col?.style, width, minWidth: width, maxWidth: width }}
      onDoubleClick={(e) => {
        // Double-clicking the header opens the column menu straight into label
        // editing (label section is first, its input focused + all-selected).
        // Ignore double-clicks on the resizer or the ⋮ menu button.
        const target = e.target as HTMLElement;
        if (target.closest('.gs-resizer, .gs-menu-btn')) {
          return;
        }
        if (prevention.hasOperation(col?.prevention, prevention.ColumnMenu)) {
          return;
        }
        e.stopPropagation();
        const inner = (e.currentTarget as HTMLElement).querySelector('.gs-th-inner') as HTMLElement | null;
        const rect = (inner ?? (e.currentTarget as HTMLElement)).getBoundingClientRect();
        const alreadySelected =
          between({ start: selectingZone.startX, end: selectingZone.endX }, x) &&
          selectingZone.startY === 1 &&
          selectingZone.endY === sheet.numRows;
        if (!alreadySelected) {
          dispatch(selectCols({ range: { start: x, end: x }, numRows: sheet.numRows }));
        }
        dispatch(setColumnMenu({ x, position: { y: rect.bottom, x: rect.left }, focusLabel: true }));
      }}
      onContextMenu={(e) => {
        if (contextMenu.length > 0) {
          e.stopPropagation();
          safePreventDefault(e);
          dispatch(setContextMenuPosition({ y: e.clientY, x: e.clientX }));
          return false;
        }
        return true;
      }}
    >
      <div
        className="gs-th-inner-wrap"
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        onMouseEnter={handleDragging}
        onMouseUp={handleDragEnd}
      >
        <div className="gs-th-inner" style={{ height: sheet.headerHeight, position: 'relative' }}>
          <ScrollHandle
            style={{
              position: 'absolute',
              zIndex: topHeaderSelecting ? -1 : 1,
            }}
            vertical={-1}
          />
          {(() => {
            const displayedLabel = getLabel(sheet, col?.label, { y: 0, x }, x) ?? colId;
            if (displayedLabel !== colId) {
              return (
                <>
                  <span className="gs-col-addr">{colId}</span>
                  {displayedLabel}
                </>
              );
            }
            return displayedLabel;
          })()}
          {!prevention.hasOperation(col?.prevention, prevention.ColumnMenu) && (
            <button
              className={`gs-menu-btn gs-column-menu-btn ${hasFilter ? 'gs-filtered' : ''} ${columnMenuState?.x === x ? 'gs-active' : ''}`}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                (e.currentTarget as HTMLElement).dataset.pressX = String(e.clientX);
                (e.currentTarget as HTMLElement).dataset.pressY = String(e.clientY);
              }}
              onMouseUp={(e) => {
                e.stopPropagation();
                const btn = e.currentTarget as HTMLElement;
                const pressX = Number(btn.dataset.pressX ?? e.clientX);
                const pressY = Number(btn.dataset.pressY ?? e.clientY);
                const moved = Math.abs(e.clientX - pressX) > 4 || Math.abs(e.clientY - pressY) > 4;
                if (moved) {
                  return; // was a drag, ignore
                }
                const rect = btn.getBoundingClientRect();
                if (columnMenuState?.x === x) {
                  dispatch(setColumnMenu(null));
                } else {
                  const alreadySelected =
                    between({ start: selectingZone.startX, end: selectingZone.endX }, x) &&
                    selectingZone.startY === 1 &&
                    selectingZone.endY === sheet.numRows;
                  if (!alreadySelected) {
                    dispatch(selectCols({ range: { start: x, end: x }, numRows: sheet.numRows }));
                  }
                  dispatch(setColumnMenu({ x, position: { y: rect.bottom, x: rect.left } }));
                }
              }}
            >
              ⋮
            </button>
          )}
          <div
            className={`
              gs-resizer 
              ${prevention.hasOperation(col?.prevention, prevention.Resize) ? 'gs-protected' : ''}
              ${dragging ? 'gs-hidden' : ''}`}
            style={{ height: sheet.headerHeight }}
            onMouseDown={handleResizeMouseDown}
          >
            <i />
          </div>
        </div>
      </div>
    </th>
  );
});
